/**
 * Wall-clock helpers for schedule evaluation.
 *
 * Every schedule decision is made in the STORE's IANA timezone, never the
 * device's. Fire TV sticks routinely finish setup with the wrong region, so the
 * device's own timezone is never consulted. Working in wall-clock terms via
 * Intl also makes daylight-saving handling automatic:
 *
 *   - Spring forward (NZ, late September): 02:00-03:00 does not exist, so a rule
 *     covering only that hour simply does not fire that day.
 *   - Fall back (NZ, early April): 02:00-03:00 happens twice, so such a rule
 *     fires twice.
 *
 * `java.time.ZonedDateTime` on the Kotlin side has identical semantics, which is
 * what keeps the two resolvers in agreement.
 */

/** ISO 8601 weekday numbering: 1 = Monday ... 7 = Sunday. */
export type IsoDayOfWeek = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  isoDayOfWeek: IsoDayOfWeek;
  /** Minutes elapsed since local midnight. */
  minuteOfDay: number;
}

const WEEKDAY_TO_ISO: Record<string, IsoDayOfWeek> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let fmt = formatterCache.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      // h23 rather than hour12:false — the latter can yield "24" for midnight
      // on some ICU builds.
      hourCycle: 'h23',
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatterCache.set(timeZone, fmt);
  }
  return fmt;
}

/** Break an instant into wall-clock parts as observed in `timeZone`. */
export function zonedParts(instantMs: number, timeZone: string): ZonedParts {
  const parts = formatterFor(timeZone).formatToParts(new Date(instantMs));
  const lookup: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== 'literal') lookup[part.type] = part.value;
  }

  const isoDayOfWeek = WEEKDAY_TO_ISO[lookup.weekday ?? ''];
  if (!isoDayOfWeek) {
    throw new Error(`Unable to read weekday for timezone "${timeZone}"`);
  }

  const hour = Number(lookup.hour);
  const minute = Number(lookup.minute);

  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour,
    minute,
    second: Number(lookup.second),
    isoDayOfWeek,
    minuteOfDay: hour * 60 + minute,
  };
}

/** True when `timeZone` is a zone this runtime actually knows. */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Parse "HH:MM" into minutes since midnight. Throws on malformed input. */
export function parseHhMm(value: string): number {
  const match = HH_MM.exec(value);
  if (!match) throw new Error(`Expected HH:MM, received "${value}"`);
  return Number(match[1]) * 60 + Number(match[2]);
}

export function isHhMm(value: string): boolean {
  return HH_MM.test(value);
}

function nextIsoDay(day: IsoDayOfWeek): IsoDayOfWeek {
  return (day === 7 ? 1 : day + 1) as IsoDayOfWeek;
}

/**
 * Does the current wall-clock moment fall inside a recurring weekly window?
 *
 * Start is inclusive, end is exclusive. A window whose end is at or before its
 * start wraps past midnight into the next day, so Friday 11:00-02:30 covers
 * Friday 11:00 through Saturday 02:30 — the normal case for a pizza store's
 * closing time. An end equal to the start therefore means a full 24 hours.
 */
export function isWithinWeeklyWindow(
  now: Pick<ZonedParts, 'isoDayOfWeek' | 'minuteOfDay'>,
  days: readonly IsoDayOfWeek[],
  startMinute: number,
  endMinute: number,
): boolean {
  const wraps = endMinute <= startMinute;

  for (const day of days) {
    if (wraps) {
      if (now.isoDayOfWeek === day && now.minuteOfDay >= startMinute) return true;
      if (now.isoDayOfWeek === nextIsoDay(day) && now.minuteOfDay < endMinute) return true;
    } else if (
      now.isoDayOfWeek === day &&
      now.minuteOfDay >= startMinute &&
      now.minuteOfDay < endMinute
    ) {
      return true;
    }
  }

  return false;
}
